import { Eye, Play, Loader2 } from 'lucide-react'
// import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import clsx from 'clsx'
import { useState, useMemo } from 'react'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { Upload } from 'lucide-react'
// import { formatCLP } from '../../utils/formatCLP'

const toCount = value => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const formatOnlyDate = value => {
  if (!value) return '—'
  return new Intl.DateTimeFormat('es-CL', {
    dateStyle: 'short'
  }).format(new Date(value))
}

const formatOnlyTime = value => {
  if (!value) return ''
  return new Intl.DateTimeFormat('es-CL', {
    timeStyle: 'short'
  }).format(new Date(value))
}

const normalizeText = value =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

const calculateDocumentTotal = doc => {
  const subtotal = (doc?.details || []).reduce((acc, item) => {
    const price = Number(item?.unit_price) || 0
    const qty = Number(item?.quantity) || 0
    const discount = Number(item?.discount_percent) || 0
    return acc + price * qty * (1 - discount / 100)
  }, 0)

  const isExenta = String(doc?.dte_type?.code || doc?.tipo_dte || '') === '34'
  const iva = isExenta ? 0 : subtotal * 0.19

  return subtotal + iva
}

const getPayloadDocument = detail => detail?.payload_enviado?.documents?.[0] || null

const getDocumentTypeCode = detail =>
  String(detail?.tipo_dte || getPayloadDocument(detail)?.dte_type?.code || '')

const getReceiverRut = detail =>
  detail?.receptor_rut || getPayloadDocument(detail)?.document_receiver?.rut || ''

const resolveDetailOutcome = detail => {
  const finalState = normalizeText(detail?.estado_final)
  const documentStatus = normalizeText(detail?.documento_emitido?.estado_documento)
  const resultMessage = normalizeText(detail?.motivo_resultado)

  if (finalState === 'success' || documentStatus === 'issued') return 'success'
  if (
    documentStatus === 'rejected' ||
    resultMessage.includes('rechazo') ||
    resultMessage.includes('rechazada') ||
    resultMessage.includes('rechazado')
  ) {
    return 'rejected'
  }

  return 'error'
}

// const getLoadStatusMeta = ({ processed, loaded, failed }) => {
//   if (processed === 0) return { label: 'Sin datos', tone: 'slate' }
//   if (loaded > 0 && failed === 0) return { label: 'Exito', tone: 'emerald' }
//   if (loaded > 0 && failed > 0) return { label: 'Cargado con error', tone: 'amber' }
//   return { label: 'Con error', tone: 'rose' }
// }

const statusToneClasses = {
  amber: 'border-amber-200 bg-amber-50 text-amber-700',
  emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  rose: 'border-rose-200 bg-rose-50 text-rose-700',
  slate: 'border-slate-200 bg-slate-100 text-slate-600',
  blue: 'border-blue-200 bg-blue-50 text-blue-700'
}

const StatusPill = ({ label, tone = 'slate' }) => (
  <span
    className={clsx(
      'inline-flex max-w-full items-center justify-center rounded-full border px-2 py-1 text-center text-[0.58rem] font-semibold uppercase leading-tight tracking-[0.12em] whitespace-normal break-words',
      statusToneClasses[tone]
    )}
    title={label}
  >
    {label}
  </span>
)

// const DocTypeChips = ({ row }) => (
//   <div className='flex flex-wrap items-center justify-center gap-1'>
//     {row.documentTypes?.length ? (
//       row.documentTypes.map(code => (
//         <span
//           key={`${row.id}-${code}`}
//           className='rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[0.58rem] font-semibold text-emerald-700'
//         >
//           {code}
//         </span>
//       ))
//     ) : (
//       <span className='text-[0.64rem] text-slate-300'>Sin tipo</span>
//     )}
//   </div>
// )

const EyeButton = ({ onClick }) => (
  <button
    type='button'
    onClick={onClick}
    className='p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors'
    aria-label='Ver reporte'
    title='Ver reporte'
  >
    <Eye size={16} />
  </button>
)

const buildRows = ({ reports, documents, uploadReport, isProcessing }) => {
  const failedCount = uploadReport?.failed ?? 0
  const processedCount = documents.length + failedCount

  // Flatten and sort remote reports by date ASC for numbering
  const allRemoteCargas = (reports || []).flatMap(group => group?.carga || [])
  const sortedRemoteCargas = [...allRemoteCargas].sort((a, b) => {
    const da = new Date(a.payloadJson?.fecha_reporte || a.reportedAt || a.createdAt).getTime()
    const db = new Date(b.payloadJson?.fecha_reporte || b.reportedAt || b.createdAt).getTime()
    return da - db
  })

  const remoteRows = sortedRemoteCargas.map((report, index) => {
    const payload = report?.payloadJson || {}
    const emissionDetails = Array.isArray(payload?.detalle_emision) ? payload.detalle_emision : []
    const detailCount = emissionDetails.length
    const processed = toCount(payload?.total_documentos) ?? detailCount

    // We still keep the metrics for internal filtering logic if needed (though columns are hidden)
    const detailMetrics = emissionDetails.reduce(
      (acc, detail) => {
        const outcome = resolveDetailOutcome(detail)
        const documentType = getDocumentTypeCode(detail)
        const receiverRut = getReceiverRut(detail)
        const payloadDoc = getPayloadDocument(detail)

        if (outcome === 'success') acc.emittedCount += 1
        else acc.rejectedCount += 1

        if (documentType) acc.documentTypes.add(documentType)
        if (receiverRut) acc.customerRuts.add(receiverRut)
        if (payloadDoc) acc.totalAmount += calculateDocumentTotal(payloadDoc)

        return acc
      },
      { emittedCount: 0, rejectedCount: 0, totalAmount: 0, documentTypes: new Set(), customerRuts: new Set() }
    )

    const emittedCount = toCount(payload?.documentos_exitosos) ?? detailMetrics.emittedCount
    const failed = toCount(payload?.documentos_fallidos) ?? Math.max(processed - emittedCount, 0)
    const documentTypes = Array.from(detailMetrics.documentTypes).sort()

    return {
      id: `remote-${report.id}`,
      source: 'remote',
      rawReport: report,
      procesoNumber: String(index + 1).padStart(3, '0'),
      loadLabel: `Proceso masivo ${String(index + 1).padStart(3, '0')}`,
      createdAt: payload?.fecha_reporte || report?.reportedAt || report?.createdAt,
      fileName: payload?.nombre_archivo || payload?.fileName || 'Sin archivo',
      loaded: emittedCount,
      failed,
      processed,
      emittedCount,
      rejectedCount: detailMetrics.rejectedCount,
      totalAmount: detailMetrics.totalAmount,
      customerCount: detailMetrics.customerRuts.size,
      documentTypes,
      statusLabel: 'Terminado',
      statusTone: 'emerald',
      searchableText: JSON.stringify(payload).toLowerCase(),
      has33: documentTypes.includes('33'),
      has34: documentTypes.includes('34')
    }
  })

  const localRows = uploadReport?.createdAt || processedCount > 0
    ? (() => {
      const nextNumber = sortedRemoteCargas.length + 1
      const localDocTypes = Array.from(new Set(documents.map(doc => String(doc?.dte_type?.code || '')).filter(Boolean))).sort()
      const localDocsStr = JSON.stringify(documents).toLowerCase()

      return [{
        id: 'latest-upload-report',
        source: 'local',
        procesoNumber: String(nextNumber).padStart(3, '0'),
        loadLabel: `Proceso masivo ${String(nextNumber).padStart(3, '0')}`,
        createdAt: uploadReport?.createdAt,
        fileName: uploadReport?.fileName || 'Sin archivo',
        loaded: documents.length,
        failed: failedCount,
        processed: processedCount,
        emittedCount: null,
        rejectedCount: failedCount > 0 ? failedCount : null,
        totalAmount: documents.reduce((acc, d) => acc + calculateDocumentTotal(d), 0),
        documentTypes: localDocTypes,
        statusLabel: 'Pendiente',
        statusTone: 'amber',
        searchableText: localDocsStr,
        has33: localDocTypes.includes('33'),
        has34: localDocTypes.includes('34')
      }]
    })()
    : []

  return [...localRows, ...remoteRows]
}

export default function TablaCargasFacturas({
  reports = [],
  documents,
  uploadReport,
  isProcessing = false,
  isLoading = false,
  onViewReport,
  handleOpenUploadModal,
  handleResumeFlowModal
}) {
  const rows = buildRows({ reports, documents, uploadReport, isProcessing })

  const [filterState, setFilterState] = useState('all') // 'all', 'loaded', 'failed'
  const [docTypeFilter, setDocTypeFilter] = useState('all') // 'all', '33', '34'
  const [sortOrder, setSortOrder] = useState('desc') // 'desc', 'asc'

  const [searchFolio, setSearchFolio] = useState('')
  const [searchRut, setSearchRut] = useState('')
  const [searchRazon, setSearchRazon] = useState('')

  const filteredAndSortedRows = useMemo(() => {
    let result = [...rows]

    const folio = searchFolio.trim().toLowerCase()
    if (folio) {
      result = result.filter(r => r.searchableText && r.searchableText.includes(folio))
    }

    const rut = searchRut.trim().toLowerCase()
    if (rut) {
      const cleanRut = rut.replace(/\./g, '')
      result = result.filter(
        r => r.searchableText && r.searchableText.replace(/\./g, '').includes(cleanRut)
      )
    }

    const razon = searchRazon.trim().toLowerCase()
    if (razon) {
      result = result.filter(r => r.searchableText && r.searchableText.includes(razon))
    }

    if (filterState === 'loaded') {
      result = result.filter(r => r.loaded > 0)
    } else if (filterState === 'failed') {
      result = result.filter(r => r.failed > 0)
    }

    if (docTypeFilter === '33') {
      result = result.filter(r => r.has33)
    } else if (docTypeFilter === '34') {
      result = result.filter(r => r.has34)
    }

    result.sort((currentRow, nextRow) => {
      // Priorizar la carga local activa/pendiente
      if (currentRow.id === 'latest-upload-report') return -1
      if (nextRow.id === 'latest-upload-report') return 1

      const currentDate = currentRow?.createdAt ? new Date(currentRow.createdAt).getTime() : 0
      const nextDate = nextRow?.createdAt ? new Date(nextRow.createdAt).getTime() : 0
      return sortOrder === 'desc' ? nextDate - currentDate : currentDate - nextDate
    })

    return result
  }, [rows, filterState, docTypeFilter, sortOrder, searchFolio, searchRut, searchRazon])

  const toggleSortDate = () => {
    setSortOrder(prev => (prev === 'desc' ? 'asc' : 'desc'))
  }

  const styles = {
    table:
      'w-full table-fixed border-collapse font-sans text-[0.6rem] leading-tight sm:text-[0.63rem] md:text-[0.68rem] xl:text-[0.72rem]',
    thead: 'sticky top-0 z-10 bg-white',
    th: 'py-2 px-1 sm:px-2 w-auto max-w-[120px] text-center break-words border-b border-gray-200 uppercase text-[0.55rem] sm:text-[0.6rem] font-bold text-gray-500',
    tr: 'border-b border-gray-100 hover:bg-gray-50 transition-colors',
    td: 'px-1 py-2 align-middle sm:px-2 sm:py-2.5 md:px-2.5 md:py-3 xl:px-3',
    boldText: 'font-bold text-[0.62rem] text-slate-900 sm:text-[0.64rem] md:text-[0.66rem] xl:text-[0.72rem]',
    text: 'text-[0.56rem] font-medium text-slate-500 sm:text-[0.58rem] md:text-[0.6rem] xl:text-[0.66rem]'
  }

  const pillInputBase =
    'border border-slate-200 hover:border-slate-300 focus:border-accent2/40 focus:outline-none focus:ring-2 focus:ring-accent2/10 px-4 py-2 rounded-full shadow-sm leading-tight text-sm bg-white text-slate-700'

  return (
    <section className='rounded-[28px] border border-slate-200 bg-white shadow-[0_20px_45px_rgba(15,23,42,0.08)]'>
      <div className='p-4 md:p-6'>
        <header className='mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between'>
          <div className='max-w-2xl'>
            <h3 className='text-lg font-semibold text-text'>Facturación masiva</h3>
          </div>

          <div className='flex flex-wrap items-center gap-2 lg:justify-end'>
            {/* {documents.length > 0 && (
              <button
                type='button'
                onClick={handleResumeFlowModal}
                className='rounded-full bg-primary px-4 py-2 text-sm text-white shadow-sm transition hover:bg-primary/90 hover:shadow-md'
              >
                Continuar pasos
              </button>
            )} */}
            <button
              type='button'
              onClick={handleOpenUploadModal}
              disabled={isProcessing}
              title={isProcessing ? 'No puedes generar emisiones masivas a menos que termine' : 'Cargar archivo'}
              className={clsx(
                'flex items-center gap-2 rounded-full text-white text-sm px-4 py-2 shadow transition-all',
                isProcessing
                  ? 'bg-slate-400 cursor-not-allowed opacity-70'
                  : 'bg-accent2 hover:shadow-lg hover:bg-opacity-90'
              )}
            >
              <Upload size={16} />
              Cargar archivo
            </button>
          </div>
        </header>

        <div className='mb-6 rounded-[22px] border border-slate-200 bg-slate-50/90 p-4'>
          <div className='flex flex-wrap items-center gap-2'>
            <select
              className={clsx(
                pillInputBase,
                'min-w-[140px] cursor-pointer appearance-none pr-10'
              )}
              value={filterState}
              onChange={e => setFilterState(e.target.value)}
            >
              <option value='all'>Todos</option>
              <option value='loaded'>Con documentos validos</option>
              <option value='failed'>Con incidencias</option>
            </select>

            <select
              className={clsx(
                pillInputBase,
                'min-w-[200px] cursor-pointer appearance-none pr-10'
              )}
              value={docTypeFilter}
              onChange={e => setDocTypeFilter(e.target.value)}
            >
              <option value='all'>Todos los documentos</option>
              <option value='33'>Factura Electronica (33)</option>
              <option value='34'>Factura Exenta (34)</option>
            </select>

            <input
              type='text'
              placeholder='Folio'
              value={searchFolio}
              onChange={e => setSearchFolio(e.target.value)}
              className={clsx(pillInputBase, 'min-w-[120px]')}
            />

            <input
              type='text'
              placeholder='RUT'
              value={searchRut}
              onChange={e => setSearchRut(e.target.value)}
              className={clsx(pillInputBase, 'min-w-[180px]')}
            />

            <input
              type='text'
              placeholder='Razon social'
              value={searchRazon}
              onChange={e => setSearchRazon(e.target.value)}
              className={clsx(pillInputBase, 'min-w-[260px]')}
            />
            {/* <button
              type='button'
              onClick={handleOpenUploadModal}
              className='flex items-center gap-2
               rounded-full bg-accent2 text-white text-sm px-4 py-2 shadow hover:shadow-lg hover:bg-opacity-90'
            >
              <Upload size={16} />
              Cargar archivo
            </button> */}
          </div>
        </div>

        <div className='overflow-x-auto'>
          <table className={styles.table}>
            <thead className={styles.thead}>
              <tr>
                <th className={clsx(styles.th, 'w-[15%]')}>Procesos</th>
                <th
                  className={clsx(styles.th, 'w-[12%] cursor-pointer select-none')}
                  onClick={toggleSortDate}
                >
                  <div className='flex items-center justify-center gap-1'>
                    <span>Fecha</span>
                    <KeyboardArrowDownIcon
                      fontSize='small'
                      className={clsx('transition-transform', {
                        'rotate-180': sortOrder === 'desc'
                      })}
                    />
                  </div>
                </th>
                <th className={clsx(styles.th, 'w-[10%]')}>Emitidas</th>
                <th className={clsx(styles.th, 'w-[12%]')}>Error</th>
                <th className={clsx(styles.th, 'w-[10%]')}>Estado</th>
                <th className={clsx(styles.th, 'w-[8%]')}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && filteredAndSortedRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className='py-12 text-center text-slate-500'>
                    Cargando informes...
                  </td>
                </tr>
              ) : filteredAndSortedRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className='py-12 text-center text-slate-500'>
                    Todavia no hay cargas registradas con estos filtros.
                  </td>
                </tr>
              ) : (
                filteredAndSortedRows.map(row => (
                  <tr key={row.id} className={styles.tr}>
                    <td className={clsx(styles.td, 'text-center')}>
                      <div className='flex flex-col items-center gap-0.5'>
                        <div className='flex flex-wrap items-center justify-center gap-1'>
                          <span className='text-[0.56rem] font-semibold text-slate-900 sm:text-[0.58rem] md:text-[0.62rem] xl:text-[0.66rem]'>
                            {row.loadLabel}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className={clsx(styles.td, 'text-center')}>
                      <div className='flex flex-col items-center leading-tight'>
                        <span className={styles.boldText}>{formatOnlyDate(row.createdAt)}</span>
                        <span className='text-xs text-slate-400'>
                          {formatOnlyTime(row.createdAt)}
                        </span>
                      </div>
                    </td>
                    <td className={clsx(styles.td, 'text-center')}>
                      <div className='flex flex-col items-center leading-tight'>
                        <span className='font-bold text-[0.54rem] text-slate-900 sm:text-[0.56rem] md:text-[0.6rem] xl:text-[0.64rem]'>
                          {row.emittedCount ?? '—'}
                        </span>
                      </div>
                    </td>
                    <td className={clsx(styles.td, 'text-center')}>
                      <div className='flex flex-col items-center leading-tight'>
                        <span className='font-bold text-[0.54rem] text-slate-900 sm:text-[0.56rem] md:text-[0.6rem] xl:text-[0.64rem]'>
                          {row.failed ?? '—'}
                        </span>
                      </div>
                    </td>
                    <td className={clsx(styles.td, 'text-center')}>
                      <StatusPill label={row.statusLabel} tone={row.statusTone} />
                    </td>
                    <td className={clsx(styles.td, 'px-0 sm:px-1 md:px-1.5 xl:px-2')}>
                      <div className='flex items-center justify-center gap-1'>
                        {row.statusLabel === 'Pendiente' ? (
                          <div className='flex items-center gap-1'>
                            {isProcessing && row.source === 'local' && (
                              <div className='p-1.5 text-blue-600' title='Procesando...'>
                                <Loader2 size={18} className='animate-spin' />
                              </div>
                            )}
                            <button
                              type='button'
                              onClick={handleResumeFlowModal}
                              className='p-1.5 text-accent2 hover:bg-accent2/10 rounded transition-colors'
                              title='Continuar pasos'
                            >
                              <Play size={16} fill='currentColor' />
                            </button>
                          </div>
                        ) : (
                          <EyeButton onClick={() => onViewReport?.(row)} />
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>)
}
